import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Code from "@tiptap/extension-code";
import { createLowlight } from "lowlight";

// Import common languages for syntax highlighting
import javascript from "highlight.js/lib/languages/javascript";
import typescript from "highlight.js/lib/languages/typescript";
import python from "highlight.js/lib/languages/python";
import java from "highlight.js/lib/languages/java";
import cpp from "highlight.js/lib/languages/cpp";
import css from "highlight.js/lib/languages/css";
import html from "highlight.js/lib/languages/xml";
import json from "highlight.js/lib/languages/json";
import sql from "highlight.js/lib/languages/sql";
import bash from "highlight.js/lib/languages/bash";
import php from "highlight.js/lib/languages/php";
import go from "highlight.js/lib/languages/go";
import rust from "highlight.js/lib/languages/rust";

// Create lowlight instance and register languages
const lowlight = createLowlight();
lowlight.register("javascript", javascript);
lowlight.register("typescript", typescript);
lowlight.register("python", python);
lowlight.register("java", java);
lowlight.register("cpp", cpp);
lowlight.register("css", css);
lowlight.register("html", html);
lowlight.register("xml", html);
lowlight.register("json", json);
lowlight.register("sql", sql);
lowlight.register("bash", bash);
lowlight.register("shell", bash);
lowlight.register("php", php);
lowlight.register("go", go);
lowlight.register("rust", rust);

const TipTapEditor = ({
  content,
  onChange,
  placeholder = "Start writing...",
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        code: false, // Disable default code extension
        codeBlock: false, // Disable default codeBlock extension
      }),
      Code.configure({
        HTMLAttributes: {
          class: "inline-code",
        },
      }),
      CodeBlockLowlight.configure({
        lowlight,
        defaultLanguage: "javascript",
        HTMLAttributes: {
          class: "code-block",
        },
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: "tiptap-editor",
      },
    },
  });

  // Update editor content when the content prop changes
  useEffect(() => {
    if (editor && content !== undefined && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-wrapper">
      {/* Toolbar */}
      <div className="tiptap-toolbar">
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`toolbar-btn ${editor.isActive("bold") ? "active" : ""}`}
            title="Bold"
          >
            <iconify-icon icon="solar:text-bold-bold"></iconify-icon>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`toolbar-btn ${
              editor.isActive("italic") ? "active" : ""
            }`}
            title="Italic"
          >
            <iconify-icon icon="solar:text-italic-bold"></iconify-icon>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleCode().run()}
            className={`toolbar-btn ${editor.isActive("code") ? "active" : ""}`}
            title="Inline Code"
          >
            <iconify-icon icon="solar:code-bold"></iconify-icon>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            className={`toolbar-btn ${
              editor.isActive("heading", { level: 1 }) ? "active" : ""
            }`}
            title="Heading 1"
          >
            H1
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            className={`toolbar-btn ${
              editor.isActive("heading", { level: 2 }) ? "active" : ""
            }`}
            title="Heading 2"
          >
            H2
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            className={`toolbar-btn ${
              editor.isActive("heading", { level: 3 }) ? "active" : ""
            }`}
            title="Heading 3"
          >
            H3
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`toolbar-btn ${
              editor.isActive("bulletList") ? "active" : ""
            }`}
            title="Bullet List"
          >
            <iconify-icon icon="solar:list-bold"></iconify-icon>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`toolbar-btn ${
              editor.isActive("orderedList") ? "active" : ""
            }`}
            title="Numbered List"
          >
            <iconify-icon icon="solar:list-check-bold"></iconify-icon>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`toolbar-btn ${
              editor.isActive("blockquote") ? "active" : ""
            }`}
            title="Quote"
          >
            <iconify-icon icon="solar:quote-up-bold"></iconify-icon>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={`toolbar-btn ${
              editor.isActive("codeBlock") ? "active" : ""
            }`}
            title="Code Block"
          >
            <iconify-icon icon="solar:code-square-bold"></iconify-icon>
          </button>
          <button
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className="toolbar-btn"
            title="Horizontal Rule"
          >
            <iconify-icon icon="solar:minus-bold"></iconify-icon>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="toolbar-btn"
            title="Undo"
          >
            <iconify-icon icon="solar:undo-left-bold"></iconify-icon>
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="toolbar-btn"
            title="Redo"
          >
            <iconify-icon icon="solar:undo-right-bold"></iconify-icon>
          </button>
        </div>
      </div>

      {/* Editor Content */}
      <div className="tiptap-content">
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
    </div>
  );
};

export default TipTapEditor;
